import { createServerFn } from "@tanstack/react-start";
import { wagsListingsV2, wagsOrganizationsV2 } from "@wags/db/schema";
import { and, count, desc, eq, isNull } from "drizzle-orm";

import { getDb } from "@/lib/core/db.server";
import type { Source } from "./network-source-data";

export const NETWORK_SOURCES_TTL_MS = 30 * 60_000;
const NETWORK_SOURCES_RETRY_MS = 60_000;

type Cached = { expiresAt: number; value: Source[] };
let cache: Cached | null = null;
let inflight: Promise<Source[]> | null = null;

type ProviderType = "shelter_rescue" | "breeder";

async function queryTopSources(): Promise<Source[]> {
    const db = await getDb();
    const eligible = and(
        eq(wagsListingsV2.status, "available"),
        eq(wagsListingsV2.hasDog, true),
        isNull(wagsListingsV2.duplicateOfListingId),
        eq(wagsListingsV2.visibility, "public"),
        eq(wagsListingsV2.hidden, false),
        eq(wagsListingsV2.publicationState, "published"),
        eq(wagsOrganizationsV2.hidden, false),
    );

    const topByKind = async (providerType: ProviderType, limit: number) => {
        const rows = await db
            .select({
                name: wagsOrganizationsV2.name,
                available: count(),
            })
            .from(wagsListingsV2)
            .innerJoin(
                wagsOrganizationsV2,
                eq(wagsListingsV2.organizationId, wagsOrganizationsV2.id),
            )
            .where(and(eligible, eq(wagsListingsV2.providerType, providerType)))
            .groupBy(wagsListingsV2.organizationId, wagsOrganizationsV2.name)
            .orderBy(desc(count()))
            .limit(limit);
        const kind: Source["kind"] =
            providerType === "breeder" ? "breeder" : "shelter";
        return rows
            .filter((r) => r.name?.trim())
            .map<Source>((r) => ({
                name: r.name,
                kind,
                dogs: Number(r.available),
            }));
    };

    const [shelters, breeders] = await Promise.all([
        topByKind("shelter_rescue", 14),
        topByKind("breeder", 8),
    ]);
    return [...shelters, ...breeders];
}

async function load(): Promise<Source[]> {
    try {
        const value = await queryTopSources();
        if (value.length > 0) {
            cache = { expiresAt: Date.now() + NETWORK_SOURCES_TTL_MS, value };
        }
        return value;
    } catch (error) {
        console.warn("[network-sources] top sources query failed", error);
        const value = cache?.value ?? [];
        cache = { expiresAt: Date.now() + NETWORK_SOURCES_RETRY_MS, value };
        return value;
    }
}

export const getNetworkSources = createServerFn({ method: "GET" })
    .inputValidator(() => undefined)
    .handler(async (): Promise<Source[]> => {
        if (cache && cache.expiresAt > Date.now()) return cache.value;

        if (!inflight) {
            inflight = load().finally(() => {
                inflight = null;
            });
        }
        return await inflight;
    });
